import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { Colors } from 'context';
import { ICareerSkillOption } from 'core';
import { SkillAutocomplete, SkillLevelChoice } from 'molecules';

interface ISkillAnswer {
  skillId: number | null;
  name: string;
  level: number;
}

export interface ISkillsWidget {
  value: unknown;
  onChange: (value: ISkillAnswer[]) => void;
}

/**
 * Поле «навыки» в анкете (ТЗ 0039).
 *
 * Предзаполняется навыками, начисленными за пройденные курсы, — это и есть
 * «подставить автоматически из курса». Студент их подтверждает или правит;
 * уровень при записи в резюме НИКОГДА не понижается, поэтому скромная оценка
 * здесь не может затереть подтверждённый школой навык.
 */
export const SkillsWidget = memo(({ value, onChange }: ISkillsWidget) => {
  const { t } = useTranslation();
  // useMemo, а не выражение: без него массив новый на каждый рендер,
  // и все useCallback ниже пересоздаются вместе с ним.
  const rows = useMemo<ISkillAnswer[]>(
    () => (Array.isArray(value) ? (value as ISkillAnswer[]) : []),
    [value],
  );

  const add = useCallback(
    (option: ICareerSkillOption) => {
      if (rows.some((row) => String(row.skillId) === String(option.id))) return;
      onChange([...rows, { skillId: Number(option.id), name: option.name, level: 3 }]);
    },
    [rows, onChange],
  );

  const addFreeText = useCallback(
    (text: string) => {
      if (rows.some((row) => row.name.toLowerCase() === text.toLowerCase())) {
        return;
      }
      onChange([...rows, { skillId: null, name: text, level: 3 }]);
    },
    [rows, onChange],
  );

  return (
    <>
      <SkillAutocomplete
        placeholder={t('careerCenter.exercise.skillPlaceholder')}
        onSelectSkill={add}
        onFreeText={addFreeText}
      />
      <List>
        {rows.map((row, index) => (
          <Row key={row.skillId ?? row.name}>
            <Name>{row.name}</Name>
            <SkillLevelChoice
              skillName={row.name}
              value={row.level}
              onChange={(level) =>
                onChange(
                  rows.map((item, position) => (position === index ? { ...item, level } : item)),
                )
              }
            />
            <Remove
              type="button"
              onClick={() => onChange(rows.filter((_item, position) => position !== index))}>
              {t('careerCenter.exercise.remove')}
            </Remove>
          </Row>
        ))}
      </List>
    </>
  );
});

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
`;

const Name = styled.span`
  min-width: 150px;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Remove = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;
